// "Compare with measurement" (phase SC2): a measured G(V) curve or G(V, X) map read from a CSV as lab software exports
// it, the gap edge of each row, and a fit of the edge against the sweep - E0·√(1 − (X/Xc)²) (a critical field, or a
// gap closing quadratically) and a straight line (a Zeeman shift: g_eff = −2·slope / μB for a field sweep).

export const MU_B_MEV = 0.057883818060;      // meV/T

export type Measurement =
  | { kind: "curve"; V: number[]; G: number[] }
  | { kind: "map"; X: number[]; V: number[]; G: number[][] };

const num = (s: string) => {
  const t = s.trim().replace(/^"|"$/g, "");
  if (t === "") return NaN;
  const v = Number(t);
  if (Number.isFinite(v)) return v;
  const m = t.match(/[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/);           // "G(V=-0.5 mV)" -> -0.5
  return m ? Number(m[0]) : NaN;
};

function split(line: string): string[] {
  if (line.includes(",")) return line.split(",");
  if (line.includes(";")) return line.split(";");
  if (line.includes("\t")) return line.split("\t");
  return line.trim().split(/\s+/);
}

/** A measurement from CSV text: a matrix (first row: a label then the bias values; each row: X then G), three columns
 * X, V, G (any order of rows), or two columns V, G. Lines starting with # are comments. Throws with a reason. */
export function parseMeasurement(text: string): Measurement {
  const rows = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l && !l.startsWith("#")).map(split);
  if (rows.length < 2) throw new Error("The file has fewer than two rows.");
  const numeric = (r: string[]) => r.every((c) => Number.isFinite(Number(c.trim())));
  const head = rows[0];
  const headerLike = !Number.isFinite(Number(head[0].trim())) || !numeric(head);
  const body = headerLike ? rows.slice(1) : rows;
  const width = body[0]?.length ?? 0;
  if (headerLike && head.length > 3) {
    const V = head.slice(1).map(num);
    if (V.some((v) => !Number.isFinite(v))) throw new Error("The first row should hold the bias values after its first cell.");
    const X: number[] = [], G: number[][] = [];
    for (const r of body) {
      if (r.length < V.length + 1) continue;
      X.push(num(r[0]));
      G.push(r.slice(1, V.length + 1).map(num));
    }
    if (!X.length) throw new Error("No data rows under the header.");
    return { kind: "map", X, V, G };
  }
  if (width === 2) {
    const V = body.map((r) => num(r[0])), G = body.map((r) => num(r[1]));
    return { kind: "curve", V, G };
  }
  if (width >= 3) {
    const tri = body.map((r) => [num(r[0]), num(r[1]), num(r[2])]).filter((t) => t.every(Number.isFinite));
    const X = [...new Set(tri.map((t) => t[0]))].sort((a, b) => a - b);
    const V = [...new Set(tri.map((t) => t[1]))].sort((a, b) => a - b);
    const G = X.map(() => V.map(() => NaN));
    for (const [x, v, g] of tri) G[X.indexOf(x)][V.indexOf(v)] = g;
    return { kind: "map", X, V, G };
  }
  throw new Error("Expected a matrix (header of bias values), three columns X, V, G, or two columns V, G.");
}

/** The gap edge of each row: the bias of the largest dI/dV at V > 0 (or at V < 0, as |V|, if there is no V > 0). */
export function gapEdges(V: number[], G: number[][]): number[] {
  const pos = V.map((v, i) => [v, i] as const).filter(([v]) => v > 0);
  const use = pos.length ? pos : V.map((v, i) => [-v, i] as const).filter(([v]) => v > 0);
  return G.map((row) => {
    let best = NaN, at = NaN;
    for (const [v, i] of use) if (Number.isFinite(row[i]) && !(row[i] <= best)) { best = row[i]; at = v; }
    return at;
  });
}

export interface ClosingFit { E0: number; Xc: number; rms: number }
export interface LineFit { a: number; b: number; rms: number }

/** Least-squares E0·√(1 − (X/Xc)²) (zero beyond Xc) to the edges: E0 in closed form for each Xc on a grid, refined. */
export function fitClosing(X: number[], E: number[]): ClosingFit | null {
  const pts = X.map((x, i) => [Math.abs(x), E[i]] as const).filter(([x, e]) => Number.isFinite(x) && Number.isFinite(e));
  if (pts.length < 3) return null;
  const xmax = Math.max(...pts.map(([x]) => x));
  if (!(xmax > 0)) return null;
  const evalXc = (Xc: number) => {
    const f = pts.map(([x]) => (x < Xc ? Math.sqrt(1 - (x / Xc) ** 2) : 0));
    const ff = f.reduce((s, v) => s + v * v, 0);
    const E0 = ff > 0 ? f.reduce((s, v, i) => s + v * pts[i][1], 0) / ff : 0;
    const rms = Math.sqrt(pts.reduce((s, [, e], i) => s + (e - E0 * f[i]) ** 2, 0) / pts.length);
    return { E0, Xc, rms };
  };
  let best = evalXc(xmax);
  let lo = 0.2 * xmax, hi = 6 * xmax;
  for (let round = 0; round < 4; round++) {
    const n = 200;
    for (let k = 0; k <= n; k++) {
      const r = evalXc(lo + ((hi - lo) * k) / n);
      if (r.rms < best.rms) best = r;
    }
    const w = (hi - lo) / 20;
    lo = Math.max(1e-9, best.Xc - w); hi = best.Xc + w;
  }
  return best;
}

export function fitLine(X: number[], E: number[]): LineFit | null {
  const pts = X.map((x, i) => [x, E[i]] as const).filter(([x, e]) => Number.isFinite(x) && Number.isFinite(e));
  const n = pts.length;
  if (n < 2) return null;
  const mx = pts.reduce((s, [x]) => s + x, 0) / n, my = pts.reduce((s, [, y]) => s + y, 0) / n;
  const sxx = pts.reduce((s, [x]) => s + (x - mx) ** 2, 0);
  if (!(sxx > 0)) return null;
  const b = pts.reduce((s, [x, y]) => s + (x - mx) * (y - my), 0) / sxx;
  const a = my - b * mx;
  const rms = Math.sqrt(pts.reduce((s, [x, y]) => s + (y - a - b * x) ** 2, 0) / n);
  return { a, b, rms };
}

/** g_eff from the slope (meV/T) of a gap edge (meV) shifting linearly with B: edge = E0 − g μB B / 2. */
export const gFromSlope = (slope: number) => (-2 * slope) / MU_B_MEV;

export function mapHeatmap(m: { X: number[]; V: number[]; G: number[][] }, title: string, ylabel: string, zmax?: number) {
  return {
    data: [{ type: "heatmap", x: m.V, y: m.X, z: m.G, colorscale: "Magma", zmin: 0, zmax, colorbar: { title: { text: "2e²/h" } } }],
    layout: { title: { text: title }, xaxis: { title: { text: "Bias V (mV)" } }, yaxis: { title: { text: ylabel } } },
  };
}

export function edgesFigure(series: { name: string; X: number[]; E: number[]; color: string; fit?: ClosingFit | null }[], xlabel: string) {
  const data: unknown[] = [];
  for (const s of series) {
    data.push({ type: "scatter", mode: "markers", name: `${s.name}: gap edge`, x: s.X, y: s.E, marker: { color: s.color, size: 6 } });
    if (s.fit) {
      const xs = Array.from({ length: 101 }, (_, k) => (Math.max(...s.X.map(Math.abs)) * k) / 100);
      data.push({ type: "scatter", mode: "lines", name: `${s.name}: E0 √(1 − (X/Xc)²)`, x: xs,
        y: xs.map((x) => (x < s.fit!.Xc ? s.fit!.E0 * Math.sqrt(1 - (x / s.fit!.Xc) ** 2) : 0)), line: { color: s.color, dash: "dash" } });
    }
  }
  return { data, layout: { title: { text: "Gap edge along the sweep" }, xaxis: { title: { text: xlabel } },
    yaxis: { title: { text: "edge (mV)" }, rangemode: "tozero" }, legend: { orientation: "h", y: -0.25 } } };
}
